import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';

import { BiConditionOperatorList } from '../../../config/bi_report';
import { BI_CONDITION_VALUELESS_OPERATORS } from '../../../utils/biCondition';
import { ChromeColorPickerUI } from '../../Interface';

const createColorRule = (sortOrder) => ({
  sortOrder,
  operator: '',
  compareValue: '',
  resultColor: null,
  isDefault: false,
});

const ColorPicker = ({ color, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <button
        type="button"
        aria-label="Eredményszín kiválasztása"
        className="rounded-full border border-gray-300 shadow w-[28px] h-[28px]"
        style={{ backgroundColor: color ?? '#ffffff' }}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      >
        <ChromeColorPickerUI
          color={color}
          onChange={(selectedColor) => onChange(selectedColor.hex)}
          removeColor={() => {
            onChange(null);
            setAnchorEl(null);
          }}
        />
      </Popover>
    </>
  );
};

const BiFieldColorDialog = ({ open, onClose, biField, onSave }) => {
  const [colorRules, setColorRules] = useState([]);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!open) return;

    setColorRules(
      (biField?.biFieldColor ?? []).map((colorRule, index) => ({
        ...colorRule,
        sortOrder: index + 1,
      }))
    );
    setValidationError('');
  }, [open, biField]);

  const updateColorRule = (index, changes) => {
    setColorRules((current) =>
      current.map((colorRule, colorRuleIndex) =>
        colorRuleIndex === index ? { ...colorRule, ...changes } : colorRule
      )
    );
  };

  const removeColorRule = (index) => {
    setColorRules((current) =>
      current
        .filter((_colorRule, colorRuleIndex) => colorRuleIndex !== index)
        .map((colorRule, colorRuleIndex) => ({ ...colorRule, sortOrder: colorRuleIndex + 1 }))
    );
  };

  const moveColorRule = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= colorRules.length) return;

    setColorRules((current) => {
      const reordered = [...current];
      [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
      return reordered.map((colorRule, colorRuleIndex) => ({ ...colorRule, sortOrder: colorRuleIndex + 1 }));
    });
  };

  const validate = () => {
    if (colorRules.filter((colorRule) => colorRule.isDefault).length > 1) {
      return 'Csak egy alapértelmezett szín adható meg!';
    }

    for (const colorRule of colorRules) {
      if (!colorRule.resultColor) return 'Minden szabályhoz eredményszín szükséges!';
      if (colorRule.isDefault) continue;
      if (!colorRule.operator) return 'Minden szabályhoz operátor szükséges!';
      if (
        !BI_CONDITION_VALUELESS_OPERATORS.includes(colorRule.operator) &&
        colorRule.compareValue === ''
      ) {
        return 'Az összehasonlítási érték megadása kötelező!';
      }
    }

    return '';
  };

  const handleSave = () => {
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }

    onSave(
      colorRules.map((colorRule, index) => ({
        ...colorRule,
        sortOrder: index + 1,
        operator: colorRule.isDefault ? null : colorRule.operator,
        compareValue:
          colorRule.isDefault || BI_CONDITION_VALUELESS_OPERATORS.includes(colorRule.operator)
            ? null
            : colorRule.compareValue,
      }))
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Cellaszínek szerkesztése – {biField?.name}</DialogTitle>
      <DialogContent className="flex flex-col gap-4 pt-2">
        <div className="flex items-center justify-between mt-2">
          <Typography variant="h6">Színezési szabályok</Typography>
          <Button
            type="button"
            startIcon={<Add />}
            onClick={() => setColorRules((current) => [...current, createColorRule(current.length + 1)])}
          >
            Szabály hozzáadása
          </Button>
        </div>

        {colorRules.map((colorRule, index) => (
          <div
            key={colorRule.id ?? index}
            className={`grid grid-cols-12 gap-3 items-center rounded px-3 py-2 ${index % 2 === 0 ? 'bg-gray-100' : ''}`}
          >
            <div className="col-span-1 flex items-center">
              <Typography>{index + 1}.</Typography>
              <div className="flex flex-col">
                <IconButton size="small" disabled={index === 0} onClick={() => moveColorRule(index, -1)}>
                  <ArrowUpward fontSize="inherit" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === colorRules.length - 1}
                  onClick={() => moveColorRule(index, 1)}
                >
                  <ArrowDownward fontSize="inherit" />
                </IconButton>
              </div>
            </div>
            <div className="col-span-3">
              <TextField
                select
                fullWidth
                label="Operátor"
                value={colorRule.operator ?? ''}
                disabled={colorRule.isDefault}
                onChange={(event) => updateColorRule(index, { operator: event.target.value })}
                variant="standard"
              >
                {BiConditionOperatorList.map((operator) => (
                  <MenuItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div className="col-span-3">
              <TextField
                fullWidth
                label="Összehasonlítási érték"
                value={colorRule.compareValue ?? ''}
                disabled={colorRule.isDefault || BI_CONDITION_VALUELESS_OPERATORS.includes(colorRule.operator)}
                onChange={(event) => updateColorRule(index, { compareValue: event.target.value })}
                variant="standard"
              />
            </div>
            <div className="col-span-2 flex flex-col items-center gap-1">
              <Typography variant="caption">Eredményszín</Typography>
              <ColorPicker
                color={colorRule.resultColor}
                onChange={(resultColor) => updateColorRule(index, { resultColor })}
              />
            </div>
            <div className="col-span-2">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(colorRule.isDefault)}
                    onChange={(event) => updateColorRule(index, { isDefault: event.target.checked })}
                  />
                }
                label="Alapértelmezett"
              />
            </div>
            <div className="col-span-1 flex justify-end">
              <IconButton color="error" onClick={() => removeColorRule(index)}>
                <Delete />
              </IconButton>
            </div>
          </div>
        ))}

        {!colorRules.length && <Typography color="text.secondary">Még nincs színezési szabály.</Typography>}
        {validationError && <Typography color="error">{validationError}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Mégse</Button>
        <Button variant="contained" onClick={handleSave}>
          Mentés
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BiFieldColorDialog;
